import i18n from 'src/lib/i18n/i18n';
import View from '@play-co/timestep-core/lib/ui/View';
import { Feature } from 'src/replicant/analytics';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import buttons from 'src/lib/ui/config/buttons';
import ButtonScaleViewWithText, {
  ButtonScaleViewWithTextOpts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import sounds from 'src/lib/sounds';
import { createUIPet } from 'src/game/pet/pet.utils';
import { StateSummary } from 'src/replicant/guest/guest.getters';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import { downscale } from 'src/conf/scale';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';

const baseButtonOpts: Partial<ButtonScaleViewWithTextOpts> = {
  width: downscale(342),
  height: downscale(128),
  fontSize: downscale(60),
  font: 'Body',
  labelOffsetX: -5,
  labelOffsetY: -2,
};

type CloseHandler = (success: boolean) => void;

export default class PopupSelectSaveConfirmation extends PopupBasicLazy<CloseHandler> {
  private readonly savesContainer: FlexBoxView;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      width: downscale(852),
      height: downscale(1180),
      zIndex: 10000,
      ...opts,
      tracking: {
        feature: Feature.NATIVE._,
        $subFeature: Feature.NATIVE.GUEST_SELECT_SAVE_CONFIRMATION,
      },
    });
    this.buttonClose.removeFromSuperview();

    const flexContainer = new FlexBoxView({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
      flex: {
        alignItems: 'center',
        flexDirection: 'column',
        paddingTop: 85,
        paddingBottom: 20,
      },
    });

    const textContainer = new FlexBoxView({
      superview: flexContainer,
      width: this.box.style.width,
      height: downscale(125),
      flex: {
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: 'column',
      },
    });

    new LangBitmapFontTextView({
      superview: textContainer,
      width: downscale(740),
      height: textContainer.style.height,
      localeText: () => i18n(`chooseSaveConfirmation.description`),
      size: downscale(34),
      wordWrap: true,
      color: '#BF8147',
      font: 'Body',
      align: 'center',
      verticalAlign: 'center',
    });

    this.savesContainer = new FlexBoxView({
      superview: flexContainer,
      width: this.box.style.width,
      height: 475,
      flex: {
        justifyContent: 'space-evenly',
        alignItems: 'center',
        marginTop: 5,
        marginBottom: 20,
      },
    });

    const ctasContainer = new FlexBoxView({
      superview: flexContainer,
      flex: {
        width: downscale(705),
        justifyContent: 'space-between',
        alignItems: 'center',
      },
    });

    new ButtonScaleViewWithText({
      superview: ctasContainer,
      ...buttons.cancel,
      ...baseButtonOpts,
      tracking: {
        feature: Feature.NATIVE._,
        $subFeature: Feature.NATIVE.GUEST_SELECT_SAVE_CONFIRM,
      },
      localeText: () => i18n(`chooseSave.confirm`),
      onClick: async () => {
        sounds.sfx.play('ok');
        opts.close(true);
      },
    });

    new ButtonScaleViewWithText({
      superview: ctasContainer,
      ...buttons.secondary,
      ...baseButtonOpts,
      tracking: {
        feature: Feature.NATIVE._,
        $subFeature: Feature.NATIVE.GUEST_SELECT_SAVE_CANCEL,
      },
      localeText: () => i18n(`basic.cancel`),
      onClick: async () => {
        sounds.sfx.play('cancel');
        opts.close(false);
      },
    });
  }

  public init(opts: { state: StateSummary }) {
    super.init({
      title: i18n(`chooseSaveConfirmation.title`),
    });

    this.createSaveItem(opts.state);
  }

  /**
   * Preload assets before showing the popup
   */
  protected getAssetsGroup() {
    return new AssetGroup([
      ...AssetGroup.constructURLs(['assets/ui/guest/']),
      ...AssetGroup.constructURLs(['assets/ui/shared/']),
      ...AssetGroup.constructURLs(['assets/ui/hud/icons/']),
    ]);
  }

  private createSaveItem(state: StateSummary) {
    // Create save item container
    const saveItem = new FlexBoxView({
      superview: this.savesContainer,
      width: downscale(368),
      height: downscale(670),
      flex: {
        justifyContent: 'space-around',
        alignItems: 'center',
        flexDirection: 'column',
      },
    });

    // Create panel background
    const bg = new ImageScaleView({
      superview: saveItem,
      width: downscale(368),
      height: downscale(611),
      image: 'assets/ui/guest/green_frame.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 30, center: 10, right: 30 },
        vertical: { top: 30, middle: 10, bottom: 30 },
      },
      imgScale: 1.7,
    });

    // Create save label
    new LangBitmapFontTextView({
      superview: saveItem,
      width: downscale(368),
      height: downscale(40),
      localeText: () =>
        i18n(`chooseSave.${state.origin}`, {
          date: new Date(state.lastPlayedAt).toLocaleDateString(),
        }),
      size: 18,
      color: '#BF8147',
      font: 'Body',
      align: 'center',
      verticalAlign: 'center',
    });

    // Create checkmark icon
    new ImageView({
      superview: bg,
      width: downscale(71),
      height: downscale(71),
      image: `assets/ui/guest/check.png`,
      centerOnOrigin: true,
      x: bg.style.width - downscale(30),
      y: downscale(30),
    });

    // Create content wrapper container
    const container = new FlexBoxView({
      superview: bg,
      width: bg.style.width,
      height: bg.style.height,
      flex: {
        justifyContent: 'space-evenly',
        alignItems: 'center',
        flexDirection: 'column',
        paddingVertical: 20,
      },
    });

    // Create animated pet avatar
    const pet = createUIPet('happy', state.species);
    pet.updateOpts({
      superview: container,
      width: 160,
      height: 150,
      centerOnOrigin: true,
      scale: 0.6,
    });

    // Create level info row
    const levelFrame = new ImageScaleView({
      superview: container,
      image: 'assets/ui/guest/frame.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 30, center: 10, right: 30 },
        vertical: { top: 30, middle: 10, bottom: 30 },
      },
      width: downscale(320),
      height: downscale(84),
    });
    new ImageView({
      superview: levelFrame,
      width: downscale(64),
      height: downscale(64),
      image: `assets/ui/guest/level.png`,
      centerOnOrigin: true,
      x: 40,
      y: levelFrame.style.height / 2,
    });
    new LangBitmapFontTextView({
      superview: levelFrame,
      width: downscale(168),
      height: 25,
      localeText: () => i18n(`match2.ui.level`, { level: state.matchLevel }),
      size: 24,
      color: '#9A7171',
      font: 'Body',
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      x: levelFrame.style.width / 2 + 20,
      y: levelFrame.style.height / 2,
    });

    // Create gems info row
    const gemsPanel = new FlexBoxView({
      superview: container,
      width: bg.style.width,
      height: 25,
      flex: {
        justifyContent: 'center',
        alignItems: 'center',
      },
    });
    new ImageScaleView({
      superview: gemsPanel,
      width: downscale(64),
      height: downscale(64),
      image: `assets/ui/hud/icons/hud_gem.png`,
      scaleMethod: 'contain',
    });
    new LangBitmapFontTextView({
      superview: gemsPanel,
      width: 100,
      height: 25,
      localeText: () => state.gems.toLocaleString(),
      size: 24,
      color: '#9A7171',
      font: 'Body',
      align: 'center',
      verticalAlign: 'center',
    });

    // Create coins info row
    const coinsPanel = new FlexBoxView({
      superview: container,
      width: bg.style.width,
      height: 25,
      flex: {
        justifyContent: 'center',
        alignItems: 'center',
      },
    });
    new ImageScaleView({
      superview: coinsPanel,
      width: downscale(64),
      height: downscale(64),
      image: `assets/ui/hud/icons/hud_coin.png`,
      scaleMethod: 'contain',
    });
    new LangBitmapFontTextView({
      superview: coinsPanel,
      width: 100,
      height: 25,
      localeText: () => state.points.toLocaleString(),
      size: 24,
      color: '#9A7171',
      font: 'Body',
      align: 'center',
      verticalAlign: 'center',
    });
  }
}
